import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell,
} from "recharts";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Loader2, TrendingUp, Zap, Clock, Repeat, ShieldAlert, Trash2, Flag,
  GitMerge, AlertTriangle, Sparkles,
} from "lucide-react";
import { API, SCOPE_CONFIG, verticalLabel } from "./kbConstants";

const PIE_COLORS = ["#0d9488", "#6366f1", "#f59e0b", "#64748b", "#ec4899", "#14b8a6"];

export default function KnowledgeAnalytics({ isAdmin }) {
  const [a, setA] = useState(null);
  const [review, setReview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sweeping, setSweeping] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [an, rv] = await Promise.all([
        axios.get(`${API}/kb/analytics`),
        axios.get(`${API}/kb/lifecycle/review`),
      ]);
      setA(an.data); setReview(rv.data);
    } catch (e) { toast.error("Could not load analytics"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const runSweep = async () => {
    setSweeping(true);
    try {
      const { data } = await axios.post(`${API}/kb/lifecycle/run`);
      toast.success(`Sweep done — ${data.expired} expired, ${data.flagged_stale} flagged stale`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Sweep failed"); }
    finally { setSweeping(false); }
  };

  if (loading || !a) {
    return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  }

  const scopeData = Object.entries(a.by_scope || {}).map(([k, v]) => ({ name: SCOPE_CONFIG[k]?.label || k, value: v }));
  const vertData = Object.entries(a.by_vertical || {}).map(([k, v]) => ({ name: verticalLabel(k), value: v }));
  const c = review?.counts || {};

  return (
    <div className="space-y-6" data-testid="kb-analytics">
      {/* Headline impact */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Metric icon={Repeat} label="Reuse rate" value={`${Math.round(a.reuse_rate * 100)}%`} sub={`${a.entries_reused} of ${a.total_active} reused`} accent="text-teal-600" />
        <Metric icon={Zap} label="Est. tokens saved" value={a.est_tokens_saved.toLocaleString()} sub={`${a.total_citations} citations`} accent="text-amber-600" />
        <Metric icon={Clock} label="Est. time saved" value={`${a.est_minutes_saved}m`} sub="across tasks" accent="text-indigo-600" />
        <Metric icon={TrendingUp} label="Avg confidence" value={`${Math.round(a.avg_confidence * 100)}%`} sub={`${a.high_confidence} high-confidence`} accent="text-emerald-600" />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <Card className="border-[var(--qn-border)]">
          <CardContent className="p-4">
            <div className="text-sm font-semibold text-[var(--qn-foreground)] mb-3">Knowledge by scope</div>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={scopeData} dataKey="value" nameKey="name" innerRadius={45} outerRadius={80} paddingAngle={2}>
                  {scopeData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
            <div className="flex flex-wrap gap-2 justify-center mt-1">
              {scopeData.map((d, i) => (
                <span key={d.name} className="text-[11px] text-slate-500 inline-flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />{d.name} ({d.value})
                </span>
              ))}
            </div>
          </CardContent>
        </Card>
        <Card className="border-[var(--qn-border)]">
          <CardContent className="p-4">
            <div className="text-sm font-semibold text-[var(--qn-foreground)] mb-3">Knowledge by vertical</div>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={vertData}>
                <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} angle={-15} textAnchor="end" height={50} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} width={24} />
                <Tooltip />
                <Bar dataKey="value" fill="#0d9488" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Reuse leaderboard */}
      <Card className="border-[var(--qn-border)]">
        <CardContent className="p-4">
          <div className="text-sm font-semibold text-[var(--qn-foreground)] mb-3 flex items-center gap-1.5">
            <Sparkles className="w-4 h-4 text-teal-600" /> Most-reused knowledge
          </div>
          {(a.top_reused || []).length === 0 ? (
            <div className="text-sm text-slate-400">No knowledge has been reused yet.</div>
          ) : (
            <div className="space-y-2" data-testid="kb-analytics-leaderboard">
              {a.top_reused.map((t, i) => (
                <div key={t.id} className="flex items-center gap-3">
                  <span className="text-xs font-mono text-slate-400 w-5">{i + 1}</span>
                  <span className="flex-1 text-sm text-[var(--qn-foreground)] truncate">{t.title}</span>
                  <Badge variant="outline" className="text-[10px]">{verticalLabel(t.vertical)}</Badge>
                  <span className="text-xs font-mono text-teal-600 w-10 text-right">{t.usage_count}×</span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Lifecycle / health */}
      <Card className="border-[var(--qn-border)]">
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div className="text-sm font-semibold text-[var(--qn-foreground)] flex items-center gap-1.5">
              <ShieldAlert className="w-4 h-4 text-amber-600" /> Lifecycle & health
            </div>
            {isAdmin && (
              <Button size="sm" variant="outline" onClick={runSweep} disabled={sweeping} className="gap-1.5" data-testid="kb-run-sweep">
                {sweeping ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />} Run cleanup
              </Button>
            )}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <HealthTile label="Pending review" value={a.health.pending_review} />
            <HealthTile label="Stale (180d+)" value={c.stale ?? a.health.stale} />
            <HealthTile label="Disputed" value={c.disputed ?? a.health.disputed} />
            <HealthTile label="Contradictions" value={c.contradictions ?? 0} />
          </div>

          {(review?.disputed || []).length > 0 && (
            <div className="space-y-1.5">
              <div className="text-[11px] uppercase tracking-wide text-slate-400 flex items-center gap-1"><Flag className="w-3 h-3" /> Disputed — needs review</div>
              {review.disputed.map((e) => (
                <div key={e.id} className="text-sm text-[var(--qn-foreground)] flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-red-400" />{e.title}
                  {e.metadata?.dispute_reason && <span className="text-xs text-slate-400 italic">— {e.metadata.dispute_reason}</span>}
                </div>
              ))}
            </div>
          )}

          {(review?.contradictions || []).length > 0 && (
            <div className="space-y-1.5">
              <div className="text-[11px] uppercase tracking-wide text-slate-400 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Potential contradictions</div>
              {review.contradictions.map((p, i) => (
                <div key={i} className="text-xs bg-amber-50 border border-amber-200 rounded-md p-2 flex items-center gap-2">
                  <GitMerge className="w-3 h-3 text-amber-600" />
                  <span className="flex-1"><b>{p.a.title}</b> ↔ <b>{p.b.title}</b></span>
                  <span className="font-mono text-amber-700">{Math.round(p.similarity * 100)}%</span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Metric({ icon: Icon, label, value, sub, accent }) {
  return (
    <Card className="border-[var(--qn-border)]">
      <CardContent className="p-4">
        <div className={`flex items-center gap-1.5 text-xs text-[var(--qn-foreground-muted)]`}><Icon className={`w-3.5 h-3.5 ${accent}`} /> {label}</div>
        <div className={`text-2xl font-bold mt-1 font-mono ${accent}`}>{value}</div>
        <div className="text-[11px] text-slate-400 mt-0.5">{sub}</div>
      </CardContent>
    </Card>
  );
}

function HealthTile({ label, value }) {
  return (
    <div className="rounded-md border border-[var(--qn-border)] p-2.5 text-center">
      <div className="text-xl font-bold font-mono text-[var(--qn-foreground)]">{value}</div>
      <div className="text-[10px] text-slate-400">{label}</div>
    </div>
  );
}
